import {css, Typography} from '@mui/material';
import {ConfigObjectType, PopupWrapper} from '@wallaby-ui/core';
import * as React from 'react';
import {useMemo, useState} from 'react';

import {usePermissions} from '../../permissions/usePermissions';
import {
  RISK_FACTOR_LABEL_COLORS,
  getRiskFactorMedicationLabel,
  groupAndSortRiskFactorsData,
  RiskFactorGroup,
} from '../hospitalizations_helpers';
import {LightPanelActionButton, TooltipWrapper, useFormatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';

type RiskFactorDto = MerieSchema.components['schemas']['RiskFactorDto'];
type TranslatedRiskFactor = RiskFactorDto & {translatedTypeOrUserComment: string};

interface Props {
  config: ConfigObjectType;
  riskFactors: RiskFactorDto[];
  oldRiskFactorIds: Set<number>;
  changeRiskFactorFilter: (newRiskFactorId: number) => void;
  /** The rows of the Deactivated group: greyed, with Restore instead of Deactivate. */
  isDeactivated: boolean;
  activateOrDeactivateRiskFactor: (riskFactorId: string, isDeactivated: boolean) => Promise<void>;
}

/** What the open confirmation is about: one risk factor, or every medication of a group (Deactivate only). */
type Confirmation = {riskFactor?: TranslatedRiskFactor; group?: RiskFactorGroup; anchor: HTMLElement};

/* the confirmation opens upwards when less than this is left below its button */
const SPACE_BELOW_FOR_POPUP = 100;

/* `.rfrow` of the alerts design: category tag, name, the single edge action on hover */
const rowCss = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 1px 10px;
  border-radius: 9px;
  min-height: 32px;
  overflow-wrap: break-word;
  &[data-sub='true'] {
    padding-left: 34px;
  }
  &:hover,
  &:has(:focus-visible),
  &[data-selected='true'] {
    background: var(--dlAlertRowHoverBackground);
  }
  & [data-name='dl-risk-factor-row--action'] {
    opacity: 0;
    transition: opacity 0.12s ease;
  }
  /* shown on hover and keyboard focus (not the focus a closed popup hands back to its button after a click), and while
     its confirmation is open: the popup is portalled out of the row */
  &:hover [data-name='dl-risk-factor-row--action'],
  &:has(:focus-visible) [data-name='dl-risk-factor-row--action'],
  &[data-selected='true'] [data-name='dl-risk-factor-row--action'] {
    opacity: 1;
  }
`;
/* `.rfcat`: the risk factor's category, on the category's colour as on the timeline; the font of the chip before the
   redesign, in the name's colour */
const tagCss = css`
  flex: none;
  font-family: var(--dlFontFamilyNormal);
  font-size: 13px;
  line-height: 16px;
  color: var(--dlThemeGray-800);
  background: var(--dlThemeGray-50);
  /* the app's chips' radius (DlMuiChip, e.g. the pharmacotherapy recommendations' patient riskiness) */
  border-radius: 2px;
  padding: 2px 9px;
  &[data-deactivated='true'] {
    color: var(--dlThemeGray-400);
    background: var(--dlThemeGray-50) !important;
  }
`;
/* `.rflink` / `.rfplain`: the name opens the timeline filtered to the factor where the factor came from MERIE. A factor
   new since the last visit stays bold, as before the redesign. The link looks and reacts as the timeline's annotation
   links (TimelineAnnotation's EntityLink): dark grey underlined, highlighted on hover and keyboard focus */
const nameCss = css`
  font-family: var(--dlFontFamilySemiBold);
  font-size: 14px;
  line-height: 1.3;
  color: var(--dlThemeGray-800);
  &[data-old='true'] {
    font-family: var(--dlFontFamilyNormal);
  }
  &[data-deactivated='true'] {
    color: var(--dlThemeGray-400);
  }
  &[data-link='true'] {
    text-decoration: underline;
    cursor: pointer;
    &:hover,
    &:focus {
      outline: none;
      color: var(--dlHighlightedFocusColor);
    }
    &:active {
      color: var(--dlHighlightedActiveColor);
    }
  }
`;
/* the tag and the name, beside the edge action: on a very narrow pane the name goes below the tag rather than breaking
   into single letters beside it, while the action keeps its place at the right edge */
const mainCss = css`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 9px;
  row-gap: 4px;
`;
/* takes the rest of the line and wraps the name in it; only when not even its longest word fits beside the tag does the
   name move to a line of its own */
const nameCellCss = css`
  flex: 1 1 0;
`;
const edgeCss = css`
  margin-left: auto;
  flex: none;
  display: flex;
`;
const emptyCss = css`
  padding: 4px 10px 8px;
  font-size: 13px;
  color: var(--dlThemeGray-400);
`;

/**
 * The rows of one group of the redesigned risk factors tab (PDM-2758): the Alerts panel's row anatomy with the risk
 * factors' content unchanged. Medications of one kind are grouped under a parent row, deactivated together from it.
 * Deactivate (red ✕) and Restore (the Alerts panel's Restore button) keep their confirmation.
 */
export const RiskFactorPanelRows: React.FC<Props> = (props) => {
  const {config, riskFactors, oldRiskFactorIds, changeRiskFactorFilter, isDeactivated, activateOrDeactivateRiskFactor} = props;
  const {t, tEnum, formatDate} = useFormatters();
  const {userRoles} = usePermissions(config);
  const canEdit = Boolean(userRoles.ENCOUNTER_DETAIL_EDIT_ROLE);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const rows = useMemo(() => {
    // the medication's translated name replaces its enum value, which getRiskFactorMedicationLabel then shows
    const translated = riskFactors.map((rf) => {
      const translatedType = tEnum('RiskFactorTypeEnum', rf.type);
      return {
        ...rf,
        translatedTypeOrUserComment: rf.type === 'OTHER' ? `${translatedType} - ${rf.typeOtherUserGivenName}` : translatedType,
        narrowTherapeuticIndexMedication: tEnum('NarrowTherapeuticIndexMedicationEnum', rf?.narrowTherapeuticIndexMedication),
      };
    }) as TranslatedRiskFactor[];
    return groupAndSortRiskFactorsData(translated, oldRiskFactorIds);
  }, [riskFactors, tEnum, oldRiskFactorIds]);

  const openConfirmation = (event: React.MouseEvent<HTMLElement>, target: Omit<Confirmation, 'anchor'>) => {
    event.preventDefault();
    event.stopPropagation();
    setConfirmation({...target, anchor: event.currentTarget});
  };
  const closeConfirmation = () => setConfirmation(null);
  const confirm = () => {
    if (confirmation?.group) {
      confirmation.group.children.forEach((child) => activateOrDeactivateRiskFactor(String(child.id), false));
    } else if (confirmation?.riskFactor) {
      activateOrDeactivateRiskFactor(String(confirmation.riskFactor.id), isDeactivated);
    }
    closeConfirmation();
  };
  const opensUp = (anchor: HTMLElement) => window.innerHeight - anchor.getBoundingClientRect().bottom < SPACE_BELOW_FOR_POPUP;

  const renderTag = (label: string | undefined) => (
    <span
      css={tagCss}
      data-deactivated={isDeactivated}
      style={label ? {backgroundColor: RISK_FACTOR_LABEL_COLORS[label]?.color} : undefined}
      data-name={'dl-risk-factor-row--tag'}
    >
      {tEnum('RiskFactorLabelEnum', label ?? '') || ''}
    </span>
  );

  const renderAction = (isSelected: boolean, onClick: (event: React.MouseEvent<HTMLElement>) => void) =>
    canEdit && (
      <div css={edgeCss}>
        <LightPanelActionButton
          iconName={isDeactivated ? 'rotateLeftIcon' : 'xmarkIcon'}
          viewBox={isDeactivated ? '0 0 512 512' : '0 0 20 20'}
          title={t(isDeactivated ? 'riskFactors.restore' : 'riskFactors.deactivate')}
          tone={isDeactivated ? 'positive' : 'negative'}
          open={isSelected}
          hideTitle={isSelected}
          action={isDeactivated ? 'RESTORE' : 'DEACTIVATE'}
          dataName={'dl-risk-factor-row--action'}
          onClick={onClick}
        />
      </div>
    );

  const renderRiskFactorRow = (riskFactor: TranslatedRiskFactor, parentGroupType?: string) => {
    const isChild = parentGroupType !== undefined;
    const medicationLabel = getRiskFactorMedicationLabel(riskFactor);
    const translatedParentGroupType = parentGroupType !== undefined ? tEnum('RiskFactorTypeEnum', parentGroupType) : null;
    const name = isChild
      ? medicationLabel ??
        (riskFactor.translatedTypeOrUserComment === translatedParentGroupType
          ? t('riskFactors.group.other')
          : riskFactor.translatedTypeOrUserComment)
      : [riskFactor.translatedTypeOrUserComment, medicationLabel].filter((v) => v).join(' - ');
    const isLink = riskFactor.createdBy === 'MERIE';
    const stateTooltip = (
      <div>
        <div>
          {t(isDeactivated ? 'riskFactors.tooltip.inactive.date' : 'riskFactors.tooltip.active.date')}:{' '}
          {formatDate(riskFactor.stateLastModifiedOn ?? riskFactor.createdOn)}
        </div>
        <div>
          {t(isDeactivated ? 'riskFactors.tooltip.inactive.user' : 'riskFactors.tooltip.active.user')}:{' '}
          {riskFactor.stateLastModifiedBy ?? riskFactor.createdBy}
        </div>
      </div>
    );
    const isSelected = confirmation?.riskFactor?.id === riskFactor.id;
    return (
      <div
        css={rowCss}
        key={riskFactor.id}
        data-sub={isChild}
        data-selected={isSelected}
        data-name={'dl-risk-factor-row'}
        data-risk-factor-id={riskFactor.id}
      >
        <div css={mainCss}>
          {!isChild && renderTag(riskFactor.labels[0])}
          <div css={nameCellCss}>
            <TooltipWrapper tooltip={stateTooltip}>
              <span
                css={nameCss}
                data-link={isLink}
                data-old={oldRiskFactorIds.has(riskFactor.id)}
                data-deactivated={isDeactivated}
                onClick={isLink ? () => changeRiskFactorFilter(riskFactor.id) : undefined}
                // reachable and opened from the keyboard as the timeline's links
                role={isLink ? 'link' : undefined}
                tabIndex={isLink ? 0 : undefined}
                onKeyDown={
                  isLink
                    ? (event) => {
                        if (event.key === 'Enter') changeRiskFactorFilter(riskFactor.id);
                      }
                    : undefined
                }
                data-name={'dl-risk-factor-row--name'}
              >
                {name}
              </span>
            </TooltipWrapper>
          </div>
        </div>
        {renderAction(isSelected, (event) => openConfirmation(event, {riskFactor}))}
      </div>
    );
  };

  const renderGroup = (group: RiskFactorGroup) => {
    const isSelected = confirmation?.group?.groupType === group.groupType;
    return (
      <React.Fragment key={group.groupType}>
        <div css={rowCss} data-selected={isSelected} data-name={'dl-risk-factor-row'} data-group-type={group.groupType}>
          <div css={mainCss}>
            {renderTag(group.label)}
            <div css={nameCellCss}>
              <span css={nameCss} data-old='true' data-deactivated={isDeactivated}>
                {tEnum('RiskFactorTypeEnum', group.groupType)}
              </span>
            </div>
          </div>
          {/* a deactivated group has no action of its own: its medications are restored one by one, as before */}
          {!isDeactivated && renderAction(isSelected, (event) => openConfirmation(event, {group}))}
        </div>
        {group.children.map((child) => renderRiskFactorRow(child as TranslatedRiskFactor, group.groupType))}
      </React.Fragment>
    );
  };

  if (!rows.length) {
    return (
      <div css={emptyCss} data-name={'dl-risk-factor-rows--empty'}>
        {t('hospitalization.detail.noRiskFactors')}
      </div>
    );
  }

  let confirmationMessage = 'riskFactors.deactivate.message';
  if (confirmation?.group) confirmationMessage = 'riskFactors.deactivateGroup.message';
  else if (isDeactivated) confirmationMessage = 'riskFactors.reactivate.message';
  return (
    <div data-name={'dl-risk-factor-rows'} data-deactivated={isDeactivated}>
      {rows.map((row) =>
        (row as RiskFactorGroup).isGroup ? renderGroup(row as RiskFactorGroup) : renderRiskFactorRow(row as TranslatedRiskFactor)
      )}
      {confirmation && (
        <PopupWrapper
          openButton={false}
          anchorTag={confirmation.anchor}
          direction={opensUp(confirmation.anchor) ? 'up' : 'down'}
          xOffset={-1}
          yOffset={6}
          showKeepDeleteButtons={!isDeactivated}
          showDiscardSaveButtons={isDeactivated}
          cancelTitle='common.confirmDialog.cancel'
          confirmTitle={isDeactivated ? 'riskFactors.restore' : 'riskFactors.deactivate'}
          onClose={closeConfirmation}
          onConfirm={confirm}
        >
          <Typography variant='sectionHeader'>{t(confirmationMessage)}</Typography>
        </PopupWrapper>
      )}
    </div>
  );
};

RiskFactorPanelRows.displayName = 'RiskFactorPanelRows';
